import { supabase } from './supabase';
import { createLogger } from './logger';

const log = createLogger('collaboration');

// 協作檢查結果介面
export interface CollaborationCheckResult {
  hasConflict: boolean;
  lastEditor?: string;
  lastModifiedAt?: Date;
  timeSinceLastEdit?: number; // 秒數
}

// 協作元數據介面
export interface CollaborationMetadata {
  id: string;
  tableName: string;
  recordId: string;
  lastModifiedBy?: string;
  lastModifiedAt: Date;
  invitationId?: string;
}

/**
 * 協作服務：處理多用戶協作的衝突檢查
 */
export const collaborationService = {
  /**
   * 檢查最近活動，用於協作衝突檢查
   * @param tableName 資料表名稱 (如: 'rsvp_responses', 'seating_arrangements')
   * @param recordId 記錄 ID
   * @param currentUserId 當前用戶 ID
   * @param conflictWindowSeconds 衝突視窗時間（秒），預設 30 秒
   */
  async checkRecentActivity(
    tableName: string,
    recordId: string,
    currentUserId: string,
    conflictWindowSeconds: number = 30
  ): Promise<CollaborationCheckResult> {
    try {
      // 查詢協作元數據
      const { data, error } = await supabase
        .from('collaboration_metadata')
        .select(`
          last_modified_by,
          last_modified_at
        `)
        .eq('table_name', tableName)
        .eq('record_id', recordId)
        .single();

      if (error && error.code !== 'PGRST116') {
        log.warn('協作檢查錯誤:', error);
        return { hasConflict: false };
      }

      // 如果沒有元數據，表示沒有衝突
      if (!data) {
        return { hasConflict: false };
      }

      const lastModifiedAt = new Date(data.last_modified_at);
      const now = new Date();
      const timeSinceLastEdit = Math.floor((now.getTime() - lastModifiedAt.getTime()) / 1000);

      // 檢查是否在衝突視窗內，且不是同一用戶
      const hasConflict = 
        data.last_modified_by !== currentUserId && 
        timeSinceLastEdit <= conflictWindowSeconds;

      // 如果有衝突，查詢編輯者姓名
      let lastEditor = '其他協作者';
      if (hasConflict && data.last_modified_by) {
        try {
          const { data: userProfile } = await supabase
            .from('user_profiles')
            .select('display_name')
            .eq('id', data.last_modified_by)
            .single();
          
          if (userProfile?.display_name) {
            lastEditor = userProfile.display_name;
          }
        } catch (userError) {
          log.warn('無法取得用戶資料:', userError);
        }
      }

      return {
        hasConflict,
        lastEditor,
        lastModifiedAt,
        timeSinceLastEdit
      };

    } catch (error) {
      log.error('協作檢查失敗:', error);
      return { hasConflict: false };
    }
  },

  /**
   * 更新協作元數據
   * @param tableName 資料表名稱
   * @param recordId 記錄 ID  
   * @param userId 用戶 ID
   * @param invitationId 邀請函 ID（可選）
   */
  async updateCollaborationMetadata(
    tableName: string,
    recordId: string,
    userId: string,
    invitationId?: string
  ): Promise<void> {
    try {
      const { error } = await supabase
        .from('collaboration_metadata')
        .upsert({
          table_name: tableName,
          record_id: recordId,
          last_modified_by: userId,
          last_modified_at: new Date().toISOString(),
          invitation_id: invitationId,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'table_name,record_id'
        });

      if (error) {
        log.warn('更新協作元數據失敗:', error);
      }
    } catch (error) {
      log.error('更新協作元數據錯誤:', error);
    }
  },

  /**
   * 批量更新協作元數據（用於座位安排等批量操作）
   * @param updates 批量更新資料
   */
  async batchUpdateCollaborationMetadata(
    updates: Array<{
      tableName: string;
      recordId: string;
      userId: string;
      invitationId?: string;
    }>
  ): Promise<void> {
    try {
      const now = new Date().toISOString();
      const upsertData = updates.map(update => ({
        table_name: update.tableName,
        record_id: update.recordId,
        last_modified_by: update.userId,
        last_modified_at: now,
        invitation_id: update.invitationId,
        updated_at: now
      }));

      const { error } = await supabase
        .from('collaboration_metadata')
        .upsert(upsertData, {
          onConflict: 'table_name,record_id'
        });

      if (error) {
        log.warn('批量更新協作元數據失敗:', error);
      }
    } catch (error) {
      log.error('批量更新協作元數據錯誤:', error);
    }
  },

  /**
   * 清理過期的協作元數據（可定期執行）
   * @param daysToKeep 保留天數，預設 30 天
   */
  async cleanupOldMetadata(daysToKeep: number = 30): Promise<void> {
    try {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);

      const { error } = await supabase
        .from('collaboration_metadata')
        .delete()
        .lt('last_modified_at', cutoffDate.toISOString());

      if (error) {
        log.warn('清理協作元數據失敗:', error);
      }
    } catch (error) {
      log.error('清理協作元數據錯誤:', error);
    }
  }
};

// 工具函數：創建防抖版本的協作檢查
export const debounce = <T extends (...args: any[]) => Promise<any>>(
  func: T,
  delay: number
): ((...args: Parameters<T>) => Promise<ReturnType<T>>) => {
  let timeoutId: NodeJS.Timeout;
  let latestResolve: (value: ReturnType<T>) => void;
  let latestReject: (reason: any) => void;

  return (...args: Parameters<T>) => {
    return new Promise<ReturnType<T>>((resolve, reject) => {
      latestResolve = resolve;
      latestReject = reject;

      clearTimeout(timeoutId);
      timeoutId = setTimeout(async () => {
        try {
          const result = await func(...args);
          latestResolve(result);
        } catch (error) {
          latestReject(error);
        }
      }, delay);
    });
  };
};

// 防抖版本的協作檢查（可選使用）
export const debouncedCollaborationCheck = debounce(
  collaborationService.checkRecentActivity,
  500
);